import { useEffect, useState } from "react";
import { apiClient } from "../../App";
import { FileText, Wrench, ExternalLink, BarChart3 } from "lucide-react";

export default function AdminContent() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/admin/content/articles")
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || "Failed to load content"); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="p-6 md:p-10" data-testid="admin-content-error">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">{error}</div>
      </div>
    );
  }

  const ready = typeof data === "object" && data !== null;

  if (!ready) {
    return (
      <div className="p-6 md:p-10 max-w-[1200px]" data-testid="admin-content-loading">
        <div className="h-8 w-40 bg-slate-200 rounded animate-pulse mb-2" />
        <div className="h-4 w-80 bg-slate-200 rounded animate-pulse mb-8" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-white border border-slate-200 rounded-xl animate-pulse mb-3" />
        ))}
      </div>
    );
  }

  const stats = data.stats || {};

  return (
    <div className="p-6 md:p-10 max-w-[1200px]" data-testid="admin-content">
      <header className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Content</h1>
        <p className="text-sm text-slate-500 mt-1">Blog articles and free tools overview.</p>
      </header>

      {/* Stats cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<FileText className="w-5 h-5" />} label="Blog Articles" value={stats.total_articles || 0} testId="admin-content-stat-articles" />
        <StatCard icon={<Wrench className="w-5 h-5" />} label="Free Tools" value={stats.total_free_tools || 0} testId="admin-content-stat-tools" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Total Generations" value={stats.total_generations || 0} testId="admin-content-stat-generations" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Free Tool Uses" value={stats.total_free_tool_uses || 0} testId="admin-content-stat-tool-uses" />
      </section>

      {/* Blog articles table */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Blog Articles</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-content-articles-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Published</th>
                  <th className="px-4 py-3 font-medium">URL</th>
                </tr>
              </thead>
              <tbody>
                {(data.blog_articles || []).map((article, i) => (
                  <tr key={article.slug} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-content-article-${article.slug}`}>
                    <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{article.title}</td>
                    <td className="px-4 py-3"><CategoryBadge category={article.category} /></td>
                    <td className="px-4 py-3 text-xs text-slate-600">{article.published}</td>
                    <td className="px-4 py-3">
                      <a
                        href={`/blog/${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-violet-600 hover:text-violet-800 text-xs"
                      >
                        <ExternalLink className="w-3 h-3" /> /blog/{article.slug}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Free tools table */}
      <section>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Free Tools</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-content-tools-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Tool Name</th>
                  <th className="px-4 py-3 font-medium">Published</th>
                  <th className="px-4 py-3 font-medium">URL</th>
                </tr>
              </thead>
              <tbody>
                {(data.free_tools || []).map((tool, i) => (
                  <tr key={tool.slug} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-content-tool-${tool.slug}`}>
                    <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{tool.title}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{tool.published}</td>
                    <td className="px-4 py-3">
                      <a
                        href={`/free-tools/${tool.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-violet-600 hover:text-violet-800 text-xs"
                      >
                        <ExternalLink className="w-3 h-3" /> /free-tools/{tool.slug}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-slate-400 mb-2">{icon}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function CategoryBadge({ category }) {
  const safe = typeof category === "string" && category.length > 0 ? category : "—";
  const colors = {
    "Featured": "bg-amber-100 text-amber-700",
    "Tier 1": "bg-blue-100 text-blue-700",
    "Character": "bg-violet-100 text-violet-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs ${colors[safe] || "bg-slate-100 text-slate-600"}`}>{safe}</span>;
}
